/**
 * Settings → Email and texts: the form, and what of it goes to the server.
 *
 * Only what changed is sent. A password or token is write-only (the server
 * never returns it), so it is sent only when somebody has typed one; an empty
 * box means "keep what's there", never "clear it".
 */
import type { ChannelsOut, ChannelsPatch } from "./types";

export interface ChannelForm {
  mail_host: string;
  /** Typed, so it is text until it is checked. */
  mail_port: string;
  mail_username: string;
  mail_password: string;
  mail_sender: string;
  mail_sender_name: string;
  mail_use_tls: boolean;
  sms_account_sid: string;
  sms_auth_token: string;
  sms_sender: string;
}

const TEXT = [
  "mail_host",
  "mail_username",
  "mail_sender",
  "mail_sender_name",
  "sms_account_sid",
  "sms_sender",
] as const;

/** Saved values as form text. Secrets start empty, always. */
export function channelForm(saved: ChannelsOut): ChannelForm {
  return {
    mail_host: saved.mail_host ?? "",
    mail_port: String(saved.mail_port),
    mail_username: saved.mail_username ?? "",
    mail_password: "",
    mail_sender: saved.mail_sender ?? "",
    mail_sender_name: saved.mail_sender_name ?? "",
    mail_use_tls: saved.mail_use_tls,
    sms_account_sid: saved.sms_account_sid ?? "",
    sms_auth_token: "",
    sms_sender: saved.sms_sender ?? "",
  };
}

export function portValid(port: string): boolean {
  const n = Number(port);
  return /^\d+$/.test(port.trim()) && n >= 1 && n <= 65535;
}

/** What differs from what's saved. An empty string clears a field. */
export function channelPatch(saved: ChannelsOut, form: ChannelForm): ChannelsPatch {
  const patch: ChannelsPatch = {};
  for (const key of TEXT) {
    const next = form[key].trim();
    if (next !== (saved[key] ?? "")) patch[key] = next;
  }
  if (portValid(form.mail_port) && Number(form.mail_port) !== saved.mail_port) {
    patch.mail_port = Number(form.mail_port);
  }
  if (form.mail_use_tls !== saved.mail_use_tls) patch.mail_use_tls = form.mail_use_tls;
  if (form.mail_password) patch.mail_password = form.mail_password;
  if (form.sms_auth_token) patch.sms_auth_token = form.sms_auth_token;
  return patch;
}
